import React from "react";
import "./index.scss";

import {
  CustomBadges,
  CustomIcon,
  ShowConfirm,
  TableNullData,
  UserInformation,
} from "../../../../components";
import Context from "../../../../context";
import PlateNumberr from "../../../../components/PlateNumberr";
import Cookies from "js-cookie";
import constants from "../../../../values/constants";
import utils from "../../../../utils";
import { useTranslation } from "react-i18next";
import RatingStars from "../../../../components/RatingStars";
import driver from "../../../FinancialScreen/screens/WithdrawalScreen/screens/Driver";

type Props = {
  data: any[];
};

const Body = ({ data }: Props) => {
  // # =================================================================================================================
  // # --------------------------------------------- Start Validate Values ---------------------------------------------

  const {
    setShowModal,
    setSelectedDriver,
    blockFunction,
    includePreviousYear,
    selectedDriverIds,
    setSelectedDriverIds,
  } = React.useContext(Context.DriverScreen);

  const { t } = useTranslation();

  const userType: string = utils.stringHexConversion.reverse(
    Cookies.get(constants.USER.TYPE)
  );

  const writePermission: string | undefined = Cookies.get(
    constants.USER.WRITE_PERMISSION
  )?.toString();

  // # ---------------------------------------------- End Validate Values ----------------------------------------------
  // # =================================================================================================================

  // * =================================================================================================================
  // * -------------------------------------------- Start Local Functions ----------------------------------------------

  function onClick(clickedElement: string, data: any) {
    if (clickedElement === "driver") {
      setSelectedDriver!(data);
      setShowModal!("SHOW_DRIVER_INFORMATION");
    } else if (clickedElement === "company") {
      setSelectedDriver!(data);
      setShowModal!("SHOW_COMPANY_INFORMATION");
    }
  }

  function onApprove(driver: any): void {
    setSelectedDriver!(driver);
    setShowModal!("APPROVE");
  }
  function onBlock(driver: any): void {
    setSelectedDriver!(driver);
    setShowModal!("BLOCK");
  }

  function onUpdate(driver: any): void {
    setSelectedDriver!(driver);
    setShowModal!("UPDATE");
  }
  function onViewBlockInfo(driver: any): void {
    setSelectedDriver!(driver);
    setShowModal!("SHOW_BLOCK_INFO");
  }
  function onShowAllService(driver: any): void {
    setSelectedDriver!(driver);
    setShowModal!("SHOW_ALL_SERVICE");
  }
  function onPayServicesPrice(driver: any): void {
    setSelectedDriver!(driver);
    setShowModal!("DRIVER_PAY_SERVICES_PRICE");
  }
  function onUnBlockInfo(driver: any): void {
    setSelectedDriver!(driver);
    setShowModal!("UN_BLOCK");
  }
  function onPayDriverDeposit(driver: any): void {
    setSelectedDriver!(driver);
    setShowModal!("DRIVER_DEPOSIT");
  }
  function onShowClosedFactor(driver: any): void {
    setSelectedDriver!(driver);
    setShowModal!("CLOSED_FACTOR");
  }
  function onDocumentsApprove(driver: any): void {
    setSelectedDriver!(driver);
    setShowModal!("APPROVE_DOCUMENTS");
  }
  function onOpenFavorite(driver: any): void {
    setSelectedDriver!(driver);
    setShowModal!('FAVORITE')
  }
  function onRemoveArchive(driver: any): void {
    setSelectedDriver!(driver);
    setShowModal!('REMOVE_ARCHIVE');
  }

  function onTransferAcademicYear(driver: any): void {
    setSelectedDriver!(driver);
    setShowModal!("TRANSFER_ACADEMIC_YEAR");
  }

  // ? Only last year's drivers can be ticked — the rest already belong to the selected year.
  function onToggleSelected(driverId: string): void {
    setSelectedDriverIds!((previous: string[]) =>
      previous.includes(driverId)
        ? previous.filter((item) => item !== driverId)
        : [...previous, driverId]
    );
  }

  // * --------------------------------------------- End Local Functions -----------------------------------------------
  // * =================================================================================================================

  // ? =================================================================================================================
  // ? ----------------------------------------------- Start Return View -----------------------------------------------

  if (data?.length <= 0 || !data) return <TableNullData />;
  return (
    <tbody id="driver-table-body-container">
      {data?.map((driver: any, index: number) => {
        return (
          <tr key={`driver-${index}`}>
            {includePreviousYear && (
              <td>
                <input
                  type="checkbox"
                  disabled={driver?.academicYearStatus !== "PREVIOUS"}
                  checked={selectedDriverIds?.includes(driver?._id) ?? false}
                  onChange={() => onToggleSelected(driver?._id)}
                />
              </td>
            )}
            <td className="pointer" onClick={() => onClick("driver", driver)}>
              <UserInformation
                avatar={driver?.avatar}
                name={driver?.firstName}
                lastName={driver?.lastName}
                phoneNumber={driver?.phoneNumber}
                isBlock={
                  driver?.schoolDriverInformation?.approved?.isApproved === false
                    ? true
                    : driver?.blocks?.length >= 1
                }
              />
            </td>
            <td className="pointer" onClick={() => onClick("company", driver)}>
              <RatingStars average={driver.driverScore?.average} count={driver.driverScore?.count} size={25}/>
            </td>
            <td className="" onClick={() => onClick("driver", driver)}>
              {driver?.schoolDriverInformation?.tagNumber
                ? driver?.schoolDriverInformation?.tagNumber
                : "نامشخص"}
            </td>
            <td onClick={() => onClick("driver", driver)}>
              <PlateNumberr
                defaultValue={driver?.driverInformation?.plateNumber}
                size="xs"
                isEditable={false}
              />
            </td>
            <td onClick={() => onClick("driver", driver)}>
              {driver?.driverInformation?.nationalCode}
            </td>
            <td>
              <div className={'documents-approve-column'}>
                {driver?.academicYearStatus === "PREVIOUS" && (
                  <>
                    <CustomBadges type={'secondary-r'} title={t("previousYearDriver")}/>
                    <CustomIcon
                      icon="fas fa-arrow-right-arrow-left"
                      hoverTitle={t("transferToCurrentYear")}
                      onClick={() => onTransferAcademicYear(driver)}
                    />
                  </>
                )}
                {driver?.schoolDriverInformation?.isArchived ?
                  (
                    <>
                      <CustomBadges type={'secondary-r'} title={'آرشیو شده'}/>
                      <CustomIcon
                        icon="fas fa-box-open"
                        hoverTitle={t("removeArchive")}
                        onClick={() => onRemoveArchive(driver)}
                      />
                    </>
                  )
                  : driver?.schoolDriverInformation?.isNew ? <CustomBadges type={'warning'} title={'جدید'}/>
                    : <CustomBadges type={'success'} title={'وفادار'}/>}
              </div>
            </td>
            {/* Status */}
            <td className="pointer" onClick={() => onClick("driver", driver)}>
              <ShowConfirm
                icon="fa-solid fa-car"
                isConfirm={driver?.schoolDriverInformation?.approved?.isApproved}
                title={t("approved")}
              />
            </td>

            <td>
              <div className="documents-approve-column">
                <CustomBadges
                  type={
                    !driver?.schoolDriverInformation?.documents
                      ? "warning"
                      : !driver?.schoolDriverInformation?.documents?.approve
                        ? "danger"
                        : "success"
                  }
                  title={
                    !driver?.schoolDriverInformation?.documents
                      ? t("DocumentsNotUploaded")
                      : !driver?.schoolDriverInformation?.documents?.approve
                        ? t("DocumentsNotApproved")
                        : t("DocumentsApproved")
                  }
                />
                {(userType === "ADMIN" ||
                    userType === "COMPANY" ||
                    userType === "MUNICIPALITY") &&
                  !driver?.schoolDriverInformation?.documents?.approve &&
                  driver?.schoolDriverInformation?.documents && (
                    <CustomIcon
                      icon="fas fa-file-check"
                      hoverTitle={t("approve")}
                      onClick={() => onDocumentsApprove(driver)}
                    />
                  )}
              </div>
            </td>
            {/* actions */}
            <td>
              {writePermission === "true" && (
                <div className="table-actions-buttons">
                  {userType === 'ADMIN' && (
                    <CustomIcon
                      icon="fas fa-star"
                      hoverTitle='علاقه مندیها'
                      onClick={() => onOpenFavorite(driver)}
                    />
                  )}
                  {(userType === "COMPANY" || userType === "ADMIN") && (
                    <>
                      <CustomIcon
                        icon="fas fa-credit-card"
                        hoverTitle={t("pay_driver_services_fee")}
                        onClick={() => onPayServicesPrice(driver)}
                      />
                      <CustomIcon
                        icon="fas fa-coins"
                        hoverTitle={t("update_driver_deposit")}
                        onClick={() => onPayDriverDeposit(driver)}
                      />
                      <CustomIcon
                        icon="fas fa-table"
                        hoverTitle={t("closed_factor")}
                        onClick={() => onShowClosedFactor(driver)}
                      />
                    </>
                  )}
                  {/* Approve Icon */}
                  {!driver?.schoolDriverInformation?.approved?.isApproved && (
                    <CustomIcon
                      icon="fas fa-check"
                      hoverTitle={t("approve")}
                      onClick={() => onApprove(driver)}
                    />
                  )}
                  {/* Edit Icon */}
                  <CustomIcon
                    icon="fas fa-pen"
                    hoverTitle={t("update")}
                    onClick={() => onUpdate(driver)}
                  />
                  <CustomIcon
                    icon="fas fa-list"
                    hoverTitle={t("showAllService")}
                    onClick={() => onShowAllService(driver)}
                  />
                  {(userType === "ADMIN" || userType === "COMPANY") &&
                    driver.blocks.length === 0 && (
                      <CustomIcon
                        icon="fas fa-ban"
                        hoverTitle={t("disApprove")}
                        onClick={() => onBlock(driver)}
                      />
                    )}

                  {driver.blocks.length > 0 && (
                    <>
                      <CustomIcon
                        icon="fas fa-shield-xmark"
                        hoverTitle={t("disApproveReason")}
                        onClick={() => onViewBlockInfo(driver)}
                      />{" "}
                      <CustomIcon
                        icon="fas fa-shield-check"
                        hoverTitle={t("unBlock")}
                        onClick={() => onUnBlockInfo(driver)}
                      />
                    </>
                  )}
                </div>
              )}
            </td>
          </tr>
        );
      })}
    </tbody>
  );

  // ? ------------------------------------------------ End Return View ------------------------------------------------
  // ? =================================================================================================================
};

export default Body;
